/**
 * Player UI Store
 * Manages player UI state including device selector and pending actions
 * Note: Hookmode/preview mode is now managed in playbackQueue store
 */

import { create } from 'zustand';


/**
 * Player UI state interface
 */
export interface PlayerUIState {
  // Player active state (controls WebSocket + REST polling globally)
  isPlayerActive: boolean;
  setIsPlayerActive: (active: boolean) => void;

  // Device selector modal state (can be triggered from anywhere via play action)
  showDeviceSelector: boolean;
  setShowDeviceSelector: (show: boolean) => void;

  // Pending play action (executed after device selection)
  pendingPlayAction: (() => void) | null;
  setPendingPlayAction: (action: (() => void) | null) => void;
  executePendingPlayAction: () => void;

  // Reset to defaults
  reset: () => void;
}

/**
 * Default player UI state
 */
const defaultState = {
  isPlayerActive: false, // Start inactive to avoid polling before user activates
  showDeviceSelector: false,
  pendingPlayAction: null as (() => void) | null,
};

/**
 * Player UI store
 * Manages player UI state including device selector and pending actions
 */
export const usePlayerUIStore = create<PlayerUIState>((set, get) => ({
  // Initial state
  ...defaultState,

  // Set player active state
  setIsPlayerActive: (active) => set({ isPlayerActive: active }),

  // Set device selector modal visibility
  setShowDeviceSelector: (show) => set({ showDeviceSelector: show }),

  // Set pending play action
  setPendingPlayAction: (action) => set({ pendingPlayAction: action }),

  // Execute pending play action and clear it
  executePendingPlayAction: () => {
    const action = get().pendingPlayAction;
    if (action) {
      set({ pendingPlayAction: null });
      action();
    }
  },

  // Reset entire store to defaults
  reset: () => {
    set(defaultState);
  },
}));
